import React from 'react'
import { PureQueryOptions } from 'apollo-client'
import { Role, hasRole } from '../roles'
import {
  UpdateStatusesAction,
  UpdateStatusesWithFilterAction
} from './update-statuses-actions'
import { ReviewWithIDsAction, ReviewWithFiltersAction } from './review-actions'
import { useOrganizationFeature } from '../organization-feature'

export interface Vulnerability {
  readonly id: string
  readonly pendingStatus?: null | {
    readonly status: string
  }
  readonly status: string
}

interface StatusByIDsProps {
  readonly vulnerabilities: Vulnerability[]
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly applicationRole: Role
  readonly menuPlacement: 'top' | 'bottom'
}

export function StatusByIDsAction({
  vulnerabilities,
  organizationID,
  refetchQueries,
  refresh,
  applicationRole,
  menuPlacement
}: StatusByIDsProps) {
  const { enabled } = useOrganizationFeature(
    organizationID,
    'approval-workflow'
  )

  if (
    enabled &&
    hasRole(applicationRole, 'RULES_ADMIN') &&
    vulnerabilities.some(({ pendingStatus }) => !!pendingStatus)
  ) {
    return (
      <ReviewWithIDsAction
        vulnerabilities={vulnerabilities}
        organizationID={organizationID}
        refetchQueries={refetchQueries}
        refresh={refresh}
        menuPlacement={menuPlacement}
      />
    )
  }

  return (
    <UpdateStatusesAction
      vulnerabilities={vulnerabilities}
      organizationID={organizationID}
      refetchQueries={refetchQueries}
      refresh={refresh}
      menuPlacement={menuPlacement}
      applicationRole={applicationRole}
      isStatusApprovalEnabled={!!enabled}
    />
  )
}

interface StatusByFiltersProps {
  readonly vulnerabilities: Vulnerability[]
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly applicationRole: Role
  readonly menuPlacement: 'top' | 'bottom'
  readonly excludedVulnerabilities: Vulnerability[]
  readonly filters: {
    readonly filterText: string
    readonly quickFilter: string
    readonly aggregations: Record<string, string[]>
  }
  readonly additionalFilters?: { readonly [key: string]: any }
}

export function StatusByFiltersAction({
  vulnerabilities,
  organizationID,
  refetchQueries,
  refresh,
  applicationRole,
  menuPlacement,
  excludedVulnerabilities,
  filters,
  additionalFilters
}: StatusByFiltersProps) {
  const { enabled } = useOrganizationFeature(
    organizationID,
    'approval-workflow'
  )

  if (
    enabled &&
    hasRole(applicationRole, 'RULES_ADMIN') &&
    vulnerabilities.some(({ pendingStatus }) => !!pendingStatus)
  ) {
    return (
      <ReviewWithFiltersAction
        excludedVulnerabilities={excludedVulnerabilities}
        filters={filters}
        additionalFilters={additionalFilters}
        organizationID={organizationID}
        refetchQueries={refetchQueries}
        refresh={refresh}
        menuPlacement={menuPlacement}
      />
    )
  }

  return (
    <UpdateStatusesWithFilterAction
      excludedVulnerabilities={excludedVulnerabilities}
      filters={filters}
      additionalFilters={additionalFilters}
      organizationID={organizationID}
      refetchQueries={refetchQueries}
      refresh={refresh}
      menuPlacement={menuPlacement}
      applicationRole={applicationRole}
      isStatusApprovalEnabled={!!enabled}
    />
  )
}
